import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { useFocusTrap } from '../../../hooks/useFocusTrap';

/**
 * #1060 (owner 2026-10-23: "certain molecules are worth far more than gold"): confirms making a
 * molecule public, or private again, naming it -- Cancel has the focus, so Enter doesn't publish.
 */
export function MoleculeVisibilityDialog({
  action,
  name,
  onConfirm,
  onCancel
}: {
  readonly action: 'publish' | 'unpublish';
  readonly name: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(true, dialogRef);
  useEscapeKey(true, onCancel);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  const key = action === 'publish' ? 'publishDialog' : 'unpublishDialog';
  return (
    <div className="export-dialog-backdrop">
      <div
        ref={dialogRef}
        className="save-drawing-dialog publish-drawing-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="molecule-visibility-title"
        data-testid="molecule-visibility-dialog"
      >
        <h2 id="molecule-visibility-title" dir="auto">
          {t(`moleculeVisibility.${key}.heading`, { name })}
        </h2>
        <p>{t(`moleculeVisibility.${key}.body`)}</p>
        <div className="persistence-actions">
          <button ref={cancelRef} type="button" onClick={onCancel}>
            {t('exportDialog.cancel')}
          </button>
          <button type="button" onClick={onConfirm}>
            {t(`moleculeVisibility.${key}.confirm`)}
          </button>
        </div>
      </div>
    </div>
  );
}
